import React, { useState, useEffect } from 'react';
import { 
  getRota, 
  getRotaRegioes, 
  getRotaEquipes 
} from '../api/dashboard';
import MapaRota from './MapaRota';

const CARD_STYLE = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: '16px',
  minWidth: '140px',
  flex: '1 1 140px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const CARD_PRINCIPAL_STYLE = {
  ...CARD_STYLE,
  border: '2px solid var(--amber)',
  background: 'rgba(245,158,11,0.05)',
  minWidth: '180px',
  flex: '1 1 180px',
};

const LABEL_STYLE = {
  fontSize: 'var(--font-sm)',
  color: 'var(--muted)',
  fontWeight: 500,
};

const VALUE_STYLE = {
  fontSize: 'var(--font-xl)',
  fontWeight: 700,
  color: 'var(--text)',
  fontFamily: 'var(--font-family)',
};

const VALUE_PRINCIPAL_STYLE = {
  ...VALUE_STYLE,
  fontSize: 'var(--font-2xl)',
  color: 'var(--amber)',
};

const CARD_SECONDARY_STYLE = {
  ...CARD_STYLE,
  flex: '1 1 140px',
};

const CARD_SECONDARY_VALUE_STYLE = {
  ...VALUE_STYLE,
  fontSize: 'var(--font-lg)',
};

const FILTRO_STYLE = {
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-end',
  flexWrap: 'wrap',
  marginBottom: '24px',
  padding: '16px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
};

const SELECT_WRAPPER = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  minWidth: '180px',
};

const LABEL_FILTRO = {
  fontSize: 'var(--font-sm)',
  color: 'var(--muted)',
  fontWeight: 500,
};

const SELECT_STYLE = {
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg)',
  color: 'var(--text)',
  fontSize: 'var(--font-base)',
  fontFamily: 'var(--font-family)',
  cursor: 'pointer',
  minWidth: '100%',
};

const SECTION_TITLE_STYLE = {
  fontSize: 'var(--font-lg)',
  fontWeight: 600,
  color: 'var(--text)',
  marginBottom: '16px',
};

const GRID_CARDS = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: '16px',
  marginBottom: '24px',
};

const TABLE_CONTAINER_STYLE = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  overflow: 'hidden',
  marginTop: '8px',
};

const TABLE_STYLE = {
  width: '100%',
  borderCollapse: 'collapse',
};

const TH_STYLE = {
  textAlign: 'left',
  padding: '12px 16px',
  fontSize: 'var(--font-sm)',
  fontWeight: 600,
  color: 'var(--muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  borderBottom: '1px solid var(--border)',
};

const TD_STYLE = {
  padding: '12px 16px',
  fontSize: 'var(--font-base)',
  color: 'var(--text)',
  borderBottom: '1px solid var(--border)',
};

const ROW_CONTAINER_STYLE = {
  display: 'flex',
  gap: '24px',
  alignItems: 'flex-start',
};

const MAP_CONTAINER_STYLE = {
  flexShrink: 0,
};

function formatUS(v) {
  return (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatNum(v) {
  return (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function AbaRota({ competencia, competencias, equipesLista }) {
  const [carregando, setCarregando] = useState(true);
  const [dados, setDados] = useState(null);
  const [regioes, setRegioes] = useState([]);
  const [equipes, setEquipes] = useState([]);
  const [regiaoSelecionada, setRegiaoSelecionada] = useState(null);
  const [equipeSelecionada, setEquipeSelecionada] = useState(null);
  const [diaSelecionado, setDiaSelecionado] = useState(null);

  // Carregar regiões disponíveis
  useEffect(() => {
    if (!competencia) return;
    getRotaRegioes(competencia)
      .then(lista => setRegioes(Array.isArray(lista) ? lista.map(r => r.regiao) : []))
      .catch(err => console.error('Erro ao carregar regiões:', err));
  }, [competencia]);

  // Carregar equipes quando região muda
  useEffect(() => {
    if (!competencia || !regiaoSelecionada) {
      setEquipes([]);
      setEquipeSelecionada(null);
      return;
    }
    getRotaEquipes(competencia, regiaoSelecionada)
      .then(lista => setEquipes(Array.isArray(lista) ? lista : []))
      .catch(err => console.error('Erro ao carregar equipes:', err));
    setEquipeSelecionada(null);
    setDiaSelecionado(null);
  }, [competencia, regiaoSelecionada]);

  // Carregar dados principais (só quando equipe + dia selecionados)
  useEffect(() => {
    if (!competencia || !equipeSelecionada || !diaSelecionado) {
      setDados(null);
      setCarregando(false);
      return;
    }
    setCarregando(true);
    getRota(competencia, regiaoSelecionada, equipeSelecionada, diaSelecionado)
      .then(setDados)
      .catch(err => console.error('Erro ao carregar rota:', err))
      .finally(() => setCarregando(false));
  }, [competencia, regiaoSelecionada, equipeSelecionada, diaSelecionado]);

  // Dados das equipes já viêm via prop (lista geral)
  // Segurança: forçar que seja array (API pode retornar objeto)
  const equipesDisponiveis = (Array.isArray(equipesLista) ? equipesLista : equipes) || [];

  if (!competencia) {
    return (
      <div style={{ padding: '48px', textAlign: 'center', color: 'var(--muted)' }}>
        Selecione uma competência para visualizar a aba Rota.
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Título da seção */}
      <h2 style={SECTION_TITLE_STYLE}>
        Rota — {competencias.find(c => c.id === String(competencia))?.nome || competencia}
      </h2>

      {/* Filtros */}
      <div style={FILTRO_STYLE}>
        <div style={SELECT_WRAPPER}>
          <label style={LABEL_FILTRO}>Região</label>
          <select
            value={regiaoSelecionada || ''}
            onChange={(e) => setRegiaoSelecionada(e.target.value || null)}
            style={SELECT_STYLE}
            disabled={!competencia}
          >
            <option value="">Selecione</option>
            <option value="AL">AL</option>
            <option value="RA">RA</option>
          </select>
        </div>

        <div style={SELECT_WRAPPER}>
          <label style={LABEL_FILTRO}>Equipe</label>
          <select
            value={equipeSelecionada || ''}
            onChange={(e) => setEquipeSelecionada(e.target.value ? parseInt(e.target.value) : null)}
            style={SELECT_STYLE}
            disabled={!regiaoSelecionada}
          >
            <option value="">Selecione equipe e dia</option>
            {equipesDisponiveis.map(e => (
              <option key={e.numvei} value={e.numvei}>
                {e.numvei} — {e.nomele} ({e.tipo})
              </option>
            ))}
          </select>
        </div>

        <div style={SELECT_WRAPPER}>
          <label style={LABEL_FILTRO}>Dia</label>
          <input
            type="date"
            value={diaSelecionado || ''}
            onChange={(e) => setDiaSelecionado(e.target.value || null)}
            style={SELECT_STYLE}
            disabled={!equipeSelecionada}
          />
        </div>

        {dados?.equipe && (
          <div style={{
            fontSize: 'var(--font-sm)',
            color: 'var(--amber)',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginLeft: 'auto'
          }}>
            🔍 Filtrando: Equipe {dados.equipe} ({dados.regiao}) — {dados.data}
          </div>
        )}
      </div>

      {/* Cards Volume + Jornada lado a lado */}
      {dados && (
        <div style={{ display: 'flex', gap: '24px', marginBottom: '24px' }}>
          {/* Cards Volume (esquerda) */}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 'var(--font-sm)', color: 'var(--muted)', marginBottom: '12px', fontWeight: 600 }}>
              Volume
            </div>
            <div style={GRID_CARDS}>
              <div style={CARD_PRINCIPAL_STYLE}>
                <div style={LABEL_STYLE}>Total US (dia)</div>
                <div style={VALUE_PRINCIPAL_STYLE}>{formatUS(dados.cards_volume.total_us)}</div>
              </div>

              <div style={CARD_SECONDARY_STYLE}>
                <div style={LABEL_STYLE}>Qtd Serviços</div>
                <div style={CARD_SECONDARY_VALUE_STYLE}>{(dados.cards_volume.qtd_servicos || 0).toLocaleString('pt-BR')}</div>
              </div>

              <div style={CARD_SECONDARY_STYLE}>
                <div style={LABEL_STYLE}>KM Rodados</div>
                <div style={CARD_SECONDARY_VALUE_STYLE}>{formatNum(dados.cards_volume.km_rodados)} km</div>
              </div>
            </div>
          </div>

          {/* Cards Jornada (meio) */}
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 'var(--font-sm)', color: 'var(--muted)', marginBottom: '12px', fontWeight: 600 }}>
              Jornada
            </div>
            <div style={GRID_CARDS}>
              <div style={CARD_SECONDARY_STYLE}>
                <div style={LABEL_STYLE}>Início Jornada</div>
                <div style={CARD_SECONDARY_VALUE_STYLE}>{dados.cards_jornada.inicio_jornada}</div>
              </div>

              <div style={CARD_SECONDARY_STYLE}>
                <div style={LABEL_STYLE}>Início Refeição</div>
                <div style={CARD_SECONDARY_VALUE_STYLE}>{dados.cards_jornada.inicio_refeicao}</div>
              </div>

              <div style={CARD_SECONDARY_STYLE}>
                <div style={LABEL_STYLE}>Fim Refeição</div>
                <div style={CARD_SECONDARY_VALUE_STYLE}>{dados.cards_jornada.fim_refeicao}</div>
              </div>

              <div style={CARD_SECONDARY_STYLE}>
                <div style={LABEL_STYLE}>Fim Jornada</div>
                <div style={CARD_SECONDARY_VALUE_STYLE}>{dados.cards_jornada.fim_jornada}</div>
              </div>

              <div style={CARD_SECONDARY_STYLE}>
                <div style={LABEL_STYLE}>Horas Trabalhadas</div>
                <div style={CARD_SECONDARY_VALUE_STYLE}>{formatNum(dados.cards_jornada.horas_trabalhadas)}h</div>
              </div>

              <div style={CARD_SECONDARY_STYLE}>
                <div style={LABEL_STYLE}>Horas Refeição</div>
                <div style={CARD_SECONDARY_VALUE_STYLE}>{formatNum(dados.cards_jornada.horas_refeicao)}h</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mapa + Tabela lado a lado */}
      {dados && (
        <div style={ROW_CONTAINER_STYLE}>
          {/* Mapa (direita, 9:16) */}
          {Array.isArray(dados.mapa) && dados.mapa.length > 0 && (
            <div style={MAP_CONTAINER_STYLE}>
              <MapaRota pontos={dados.mapa} height={480} />
            </div>
          )}

          {/* Tabela (lado esquerdo, full height) */}
          <div style={{ flex: 1 }}>
            <div style={TABLE_CONTAINER_STYLE}>
              <table style={TABLE_STYLE}>
                <thead>
                  <tr>
                    <th style={TH_STYLE}>Nome</th>
                    <th style={TH_STYLE}>Serviço</th>
                    <th style={TH_STYLE}>Tipo</th>
                    <th style={TH_STYLE}>Local</th>
                    <th style={TH_STYLE}>Hora Fim</th>
                    <th style={TH_STYLE}>Tipo Atend.</th>
                    <th style={TH_STYLE}>US</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(dados.tabela) && dados.tabela.length > 0 ? (
                    dados.tabela.map((row, idx) => (
                      <tr key={idx}>
                        <td style={TD_STYLE}>{row.nomele}</td>
                        <td style={TD_STYLE}>{row.numserv}</td>
                        <td style={TD_STYLE}>{row.tipo_serv}</td>
                        <td style={TD_STYLE}>{row.local}</td>
                        <td style={TD_STYLE}>{row.hora_fim}</td>
                        <td style={TD_STYLE}>{row.tipatend || ' — '}</td>
                        <td style={TD_STYLE}>{formatUS(row.ustotal)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ ...TD_STYLE, textAlign: 'center', color: 'var(--muted)' }}>
                        Sem serviços para este dia
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}